/**
 * useLineUsersForReservations — 表示中の予約に紐づく LINE ユーザーを補う（2026-10-10）
 *
 * 背景: useLineChat の users（api_v2 get_users）は「非アーカイブの上位500人」
 * （ピン留め→未読→最終メッセージ順）しか返さない。LINE ユーザーは 3,000 人超で、最近やり取りの
 * 無い顧客やアーカイブ済みの顧客は一覧から漏れるため、daylist / PWA で
 * **紐付け済みなのに「LINE未連携」と表示されていた**（紐付けの7割超が対象）。
 *
 * 対策: 表示中の予約IDのうち一覧で見つからないものだけを get_users_by_reservations で直接引き、
 * 一覧に足して返す。呼び出し側は戻り値を従来の lineUsers の代わりに使えばよい。
 */
import { useEffect, useMemo, useState } from 'react';
import { getLineApiUrl } from '../config/backend';
import { logEvent } from '../lib/eventLog';

const LINE_API_URL = getLineApiUrl();
/** 未読数などを追従させるための再取得間隔 */
const REFRESH_MS = 60_000;

type LineUser = { line_user_id: string; reservation_ids?: (string | number)[]; [k: string]: any };

/** 一覧に紐付けが無い予約IDだけを返す（STAFF: は対象外・重複除去・ソート済み） */
export function missingReservationIds(users: LineUser[] | null | undefined, reservationIds: (string | number | null | undefined)[]): string[] {
  const known = new Set<string>();
  (Array.isArray(users) ? users : []).forEach((u) => (u.reservation_ids || []).forEach((r) => known.add(String(r))));
  const out = new Set<string>();
  reservationIds.forEach((r) => {
    if (r === null || r === undefined) return;
    const s = String(r);
    if (s !== '' && !s.startsWith('STAFF:') && !known.has(s)) out.add(s);
  });
  return Array.from(out).sort();
}

/** 一覧に補完分を足す（同じ LINE ユーザーは一覧側を優先） */
export function mergeLineUsers(base: LineUser[] | null | undefined, extras: LineUser[]): LineUser[] {
  const list = Array.isArray(base) ? base : [];
  if (extras.length === 0) return list;
  const seen = new Set(list.map((u) => u.line_user_id));
  return [...list, ...extras.filter((u) => !seen.has(u.line_user_id))];
}

export function useLineUsersForReservations(
  baseUsers: LineUser[] | null | undefined,
  reservationIds: (string | number | null | undefined)[],
): LineUser[] {
  const [extras, setExtras] = useState<LineUser[]>([]);
  const missing = missingReservationIds(baseUsers, reservationIds);
  const key = missing.join(',');

  useEffect(() => {
    if (!LINE_API_URL || key === '') {
      setExtras([]);
      return;
    }
    let alive = true;
    const ids = key.split(',');
    const load = async () => {
      try {
        // api_key= は社内 API の目印（中身は空）。fetch 時にスタッフの Bearer に置き換わる（lib/internalApiAuth.ts）
        const res = await fetch(`${LINE_API_URL}?action=get_users_by_reservations&api_key=`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reservation_ids: ids }),
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data?.success) {
          logEvent({ action: 'line.users_by_reservations', target: ids.length, result: 'fail', err: `HTTP ${res.status} ${data?.error ?? ''}` });
          return;
        }
        if (alive) setExtras(Array.isArray(data.users) ? data.users : []);
      } catch (err) {
        logEvent({ action: 'line.users_by_reservations', target: ids.length, result: 'fail', err });
      }
    };
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [key]);

  return useMemo(() => mergeLineUsers(baseUsers, extras), [baseUsers, extras]);
}
